import { Box, Button, Paper, TableContainer, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Chip from '../../components/Chip/Chip';
import DefaultTable from '../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../components/DefaultTable/TableToolbar/TableToolbar';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import GlobalSearch from '../../components/GlobalSearch/GlobalSearch';
import PageTitle from '../../components/PageTitle/PageTitle';
import { InvoiceStatus } from '../../enums/invoiceStatus';
import { useShowFilterOrSearch } from '../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../hooks/useTableFunctions';
import { useUserRoles } from '../../hooks/useUserRoles';
import { HeadCell } from '../../models/headCell';
import { Invoice } from '../../models/invoice';
import { Pagination } from '../../models/pagination';
import { INVOICES } from '../../router/constants';
import { invoiceService } from '../../services/invoiceService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../utils/helpers';
import calculateInvoiceTotal from '../../utils/invoiceTotal';
import { getRoundStatusStyle } from '../../utils/statusRoundStyle';
import FilterInvoices, { FilterInvoicesForm } from './FilterInvoices';
import ApproveInvoiceModal from './InvoiceDetails/ApproveInvoiceModal';

const initialInvoicesColumnData: HeadCell[] = [
  {
    id: 'id',
    label: 'Invoice #',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice.invoiceNumber
  },
  {
    id: 'user.firstName',
    label: 'Talent',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice?.talentFullName
  },
  {
    id: 'user.company.name',
    label: 'Company',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => invoice.companyName
  },
  {
    id: 'total',
    label: 'Total Invoice',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => (
      <FormatCurrency value={calculateInvoiceTotal(invoice)} />
    )
  },
  {
    id: 'createdOn',
    label: 'Submitted on',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) =>
      format(new Date(invoice.createdOn), 'MMM do, Y')
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    render: (invoice: Invoice) => (
      <Box sx={getRoundStatusStyle(InvoiceStatus[invoice.status])}>
        {InvoiceStatus[invoice.status]}
      </Box>
    )
  }
];

const InvoicesList = (): JSX.Element => {
  const { isTalent } = useUserRoles();
  const navigate = useNavigate();
  const location = useLocation();
  const initialFilterData: FilterInvoicesForm = {
    talent: [],
    invoiceNumber: '',
    company: '',
    startDate: null,
    endDate: null,
    status: location.state ? ['SUBMITTED'] : [],
    total: ''
  };
  const [paginationInvoices, setPaginationInvoices] = useState<
    Pagination<Invoice> | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [selected, setSelected] = useState<Invoice[]>([]);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterInvoicesForm>(
    initialInvoicesColumnData,
    initialFilterData,
    'createdOn',
    'desc'
  );
  const [openApproveModal, setOpenApproveModal] = useState(false);

  useEffect(() => {
    getInvoices();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const getInvoices = () => {
    invoiceService
      .getInvoices(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationInvoices(response.data);
        setLoading(false);
        setSelected([]);
      });
  };

  const onRowSelect = (invoices: Invoice[]) => {
    setSelected(invoices);
  };

  const showCheckBox = (invoice: Invoice): boolean =>
    invoice.status === 'SUBMITTED';

  const handleExport = () => {
    invoiceService.exportInvoices(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'invoices');
    });
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talent) {
      body = {
        ...body,
        talent: filterData.talent
          ? filterData.talent.map((talent) => talent.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }

    return body;
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Invoices</PageTitle>
        <Chip size={paginationInvoices?.totalElements} />
        <Box flexGrow={1} />
        <Button
          sx={{ mb: 3 }}
          disabled={!selected.length}
          onClick={() => setOpenApproveModal(true)}
        >
          Approve Invoices
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
        >
          {selected.length > 0 && (
            <Typography
              color="inherit"
              variant="subtitle1"
              component="div"
              mb={1}
            >
              {selected.length} selected
            </Typography>
          )}
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <FilterInvoices
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationInvoices?.content}
          rowClick={(invoice: Invoice) => navigate(`${INVOICES}/${invoice.id}`)}
          sortProps={{ sortBy, sortType, onRequestSort }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationInvoices?.totalElements
          }}
          checkBoxes={
            !isTalent
              ? {
                  selectRows: onRowSelect,
                  showCheckBox
                }
              : {}
          }
        />
        {!loading && !paginationInvoices?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No Invoices found!
          </Typography>
        )}
      </TableContainer>
      <ApproveInvoiceModal
        openModal={openApproveModal}
        closeModal={() => setOpenApproveModal(false)}
        invoiceIds={selected.map((invoice) => invoice.id)}
        onSuccess={getInvoices}
      />
    </>
  );
};

export default InvoicesList;
